import { Grid, Typography } from '@mui/material';
import { ALL, Lite, QueryFilterOperators } from 'types/api';
import { ShipmentLineSchema, ShipmentSchema } from 'types/clientV2/shipments';
import { statusTextMapper } from 'constants/datagrid';
import {
  useGetShipmentLinesQuery,
  useGetShipmentsClientV2Query,
  useUpdateShipmentsMutation,
} from 'store/slices/clientV2/shipments';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useIsCofactr } from 'hooks/useIsCofactr';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import Chip from 'ui-component/extended/Chip';
import { statusColorCallback } from 'ui-component/DataGrid/GridColTypeDef';
import EditInline from 'ui-component/EditInline';
import { HookFormQuillInput } from 'ui-component/HookFormComponents/HookFormQuillInput';
import InfoSectionItem, {
  InfoSectionItemFormatTypes,
} from 'ui-component/InfoSectionItem';
import ShipmentLineStockLotsGrid from 'views/kitting/Kit/RelatedShipmentTab/ShipmentLineStockLotsGrid';
import AddressCard from 'ui-component/cards/AddressCard';
import {
  StockDocumentRelatedObjectSchema,
  StockDocumentRelatedObjectWithStockDocument,
} from 'types/clientV2/stockDocuments';
import { useGetStockDocumentRelatedObjectsQuery } from 'store/slices/clientV2/stockDocuments';
import { DatagridFilterOperatorsValues } from 'types/datagrid';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useState } from 'react';
import { useDialogManager } from 'hooks/useDialogManager';
import { DETAIL_VIEW_DIALOGS } from 'ui-component/DetailLayout';
import GenerateDocumentButton from 'ui-component/StockDocuments/GenerateDocumentButton';
import _ from 'lodash';
import { actionButtonSpacing } from 'constants/themeConstants';
import { ALL_RECORD_TYPE_IDS } from 'constants/recordTypes';
import StockDocumentsDialog from 'ui-component/StockDocuments/StockDocumentsDialog';

export type ShipmentSchemaExpanded = Lite<
  ShipmentSchema,
  'shipTo' | 'shipFrom'
>;

type RelatedShipmentTabProps = {
  shipmentId: string;
  shipmentLines?: DecoratedKitRequestLine['shipmentLines'];
  isLoadingShipmentLines?: boolean;
};

export type ShipmentLineKitSubschema = Lite<ShipmentLineSchema, 'stockLot'>;

export default function RelatedShipmentTab({
  shipmentId,
  shipmentLines,
  isLoadingShipmentLines,
}: RelatedShipmentTabProps) {
  const { isCofactr } = useIsCofactr();
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  );
  const { openDialog, closeDialog, isDialogOpen } = useDialogManager(
    Object.values(DETAIL_VIEW_DIALOGS)
  );

  const {
    data: { data: [shipment] } = { data: [] },
    isLoading: isLoadingShipment,
  } = useServerSideQuery<ShipmentSchema, ShipmentSchemaExpanded>(
    useGetShipmentsClientV2Query,
    {
      schema: [
        ALL,
        {
          shipTo: [],
          shipFrom: [],
          address: [],
          internalMetadata: [],
          externalMetadata: [],
        },
      ],
      filters: [
        {
          field: 'id',
          operator: QueryFilterOperators.is,
          value: shipmentId,
        },
      ],
    }
  );

  const { data: shipmentLinesData, isLoading: isLoadingShipmentLinesData } =
    useServerSideQuery<ShipmentLineSchema, ShipmentLineKitSubschema>(
      useGetShipmentLinesQuery,
      shipmentId && !shipmentLines
        ? {
            filters: [
              {
                field: 'shipment',
                operator: QueryFilterOperators['='],
                value: shipmentId,
              },
            ],
            schema: [ALL, { stockLot: [] }],
          }
        : skipToken
    );

  const isLoadingLines = isLoadingShipmentLinesData || isLoadingShipmentLines;
  const lines = shipmentLinesData?.data || shipmentLines || [];

  const { data: stockDocuments, isLoading: isLoadingStockDocuments } =
    useServerSideQuery<
      StockDocumentRelatedObjectSchema,
      StockDocumentRelatedObjectWithStockDocument
    >(
      useGetStockDocumentRelatedObjectsQuery,
      shipmentId
        ? {
            filters: [
              {
                field: 'relatedObjectId',
                operator: DatagridFilterOperatorsValues.is,
                value: shipmentId,
              },
            ],
            schema: [
              ALL,
              {
                stockDocument: [],
              },
            ],
          }
        : skipToken
    );

  const generatedStockDocuments: StockDocumentRelatedObjectWithStockDocument[] =
    _.chain(stockDocuments?.data || [])
      .filter((d) => d.stockDocument.createdFromObjectId === shipmentId)
      .uniqBy((d) => d.stockDocument.id)
      .value();

  return (
    <Grid container spacing={2}>
      <Grid size={4}>
        <InfoSectionItem
          caption="Status"
          formatType={InfoSectionItemFormatTypes.children}
          isLoading={isLoadingShipment}
        >
          <Chip
            label={statusTextMapper[shipment?.status]}
            chipcolor={statusColorCallback(statusTextMapper[shipment?.status])}
            style={{ textTransform: 'uppercase' }}
            sx={{ mb: 2 }}
          />
        </InfoSectionItem>
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Shipped From"
          isLoading={isLoadingShipment}
          formatType={InfoSectionItemFormatTypes.children}
          value
        >
          {shipment?.shipFrom ? (
            <AddressCard location={shipment?.shipFrom} />
          ) : (
            <Typography variant="h4">Not specified</Typography>
          )}
        </InfoSectionItem>
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Shipped At"
          formatType={InfoSectionItemFormatTypes.dateMedium}
          isLoading={isLoadingShipment}
          value={shipment?.shippedAt}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Delivered At"
          formatType={InfoSectionItemFormatTypes.dateMedium}
          isLoading={isLoadingShipment}
          value={shipment?.receivedAt}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Carrier/Service"
          formatType={InfoSectionItemFormatTypes.string}
          isLoading={isLoadingShipment}
          value={`${shipment?.carrier ?? '-'} - ${shipment?.service ?? '-'}`}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Tracking Number"
          formatType={InfoSectionItemFormatTypes.trackingNumber}
          isLoading={isLoadingShipment}
          value={shipment?.trackingNumber}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Generate Documents"
          formatType={InfoSectionItemFormatTypes.documents}
          isLoading={isLoadingStockDocuments}
          clipboard={false}
          documentsValue={generatedStockDocuments}
          documentsOnClick={() => {
            setSelectedDocumentId(null);
            openDialog(DETAIL_VIEW_DIALOGS.GENERATED_DOCUMENTS);
          }}
        >
          <GenerateDocumentButton
            size="small"
            recordType={ALL_RECORD_TYPE_IDS.SHIPMENT}
            isLoading={isLoadingStockDocuments}
            additionalGenerateProps={{ shipmentId }}
            boxSx={
              generatedStockDocuments?.length
                ? { mt: actionButtonSpacing }
                : undefined
            }
          />
        </InfoSectionItem>
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Notes"
          formatType={InfoSectionItemFormatTypes.richText}
          isLoading={isLoadingShipment}
          value={shipment?.externalNotes ?? '-'}
          editInline={
            <EditInline
              useMutation={useUpdateShipmentsMutation}
              name="externalNotes"
              hideLabel
              value={shipment?.externalNotes}
              id={shipment?.id}
              submitAsClientV2Api
              fullHeight
            >
              <HookFormQuillInput disableRequiredProps hideLabel />
            </EditInline>
          }
        />
      </Grid>
      {isCofactr && (
        <Grid size={4}>
          <InfoSectionItem
            caption="Internal Notes"
            isLoading={isLoadingShipment}
            formatType={InfoSectionItemFormatTypes.richText}
            value={shipment?.internalNotes}
            editInline={
              <EditInline
                useMutation={useUpdateShipmentsMutation}
                name="internalNotes"
                label="Internal Notes"
                hideLabel
                value={shipment?.internalNotes}
                id={shipment?.id}
                submitAsClientV2Api
                fullHeight
              >
                <HookFormQuillInput disableRequiredProps hideLabel />
              </EditInline>
            }
          />
        </Grid>
      )}
      <Grid size={12}>
        <ShipmentLineStockLotsGrid
          shipment={shipment}
          lines={lines}
          disabled={isLoadingLines ?? false}
          isLoading={isLoadingLines}
          showMerge={false}
        />
      </Grid>
      <StockDocumentsDialog
        open={isDialogOpen(DETAIL_VIEW_DIALOGS.GENERATED_DOCUMENTS)}
        onClose={closeDialog}
        selectedDocumentId={selectedDocumentId}
        setSelectedDocumentId={setSelectedDocumentId}
        title="Generate Documents"
        documents={generatedStockDocuments}
        disableDelete
      >
        <GenerateDocumentButton
          size="small"
          recordType={ALL_RECORD_TYPE_IDS.SHIPMENT}
          isLoading={isLoadingStockDocuments}
          additionalGenerateProps={{ shipmentId }}
          boxSx={
            generatedStockDocuments.length || isLoadingStockDocuments
              ? { mt: actionButtonSpacing }
              : undefined
          }
          setSelectedDocumentId={setSelectedDocumentId}
        />
      </StockDocumentsDialog>
    </Grid>
  );
}
